/**
 * Un mensaje del hilo (§15.3). A la izquierda lo que escribió la persona; a la derecha lo que salió de la
 * marca, con un color por origen: la campaña (verde), la respuesta automática (punteada) y lo que mandó
 * alguien del equipo (violeta), que lleva quién fue, cómo le fue y cuánto costó.
 *
 * El texto se muestra igual que en la previa de las plantillas (`renderWhatsAppMarkdown`). Cada burbuja
 * lleva `id="item-<id>"`: es el ancla de «Respondió a este mensaje» y de «Ver mensajes anteriores».
 */

import type { ReactNode } from "react";
import { CornerDownRight, ExternalLink, Forward, MapPin } from "lucide-react";
import type { ItemHilo, ItemHiloEntrante, ItemHiloManual } from "../../types.conversaciones";
import { renderWhatsAppMarkdown } from "../PreviaWhatsApp";
import { ChipEstadoMensaje } from "../ChipEstadoMensaje";
import { MedioEntrante } from "./MedioEntrante";
import { TEXTO_CUPO_SIN_CARGO, TEXTO_ESTADO_ENVIO, horaNegocio } from "../../logic/conversaciones";
import { nombreDeAutorPara, usdPorMensaje } from "../../logic/formato";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

const chip = "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap";

function Texto({ texto }: { texto: string }) {
  return <div className="text-sm leading-snug whitespace-pre-wrap break-words text-slate-900">{renderWhatsAppMarkdown(texto)}</div>;
}

function Caja({ id, lado, cls, children }: { id: string; lado: "izquierda" | "derecha"; cls: string; children: ReactNode }) {
  return (
    <div id={`item-${id}`} className={`flex ${lado === "derecha" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${cls}`}>{children}</div>
    </div>
  );
}

function Pie({ children }: { children: ReactNode }) {
  return <div className="mt-1 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[11px] text-slate-500">{children}</div>;
}

export function Burbuja({ item, onReabrir, onReintentar }: {
  item: ItemHilo;
  onReabrir: () => void;
  /** «Volver a intentar»: carga el texto en la caja (con una clave nueva: la anterior ya tuvo `ok:true`). */
  onReintentar: (texto: string) => void;
}) {
  switch (item.tipo) {
    case "campana":
      return (
        <Caja id={item.id} lado="derecha" cls="bg-emerald-50 border border-emerald-100">
          <p className="mb-1 text-[11px] font-semibold text-emerald-800">{item.campana ? `Campaña «${item.campana.nombre}»` : "Campaña"}</p>
          <Texto texto={item.texto} />
          <Pie><span>{horaNegocio(item.at)}</span><ChipEstadoMensaje estado={item.estado} /></Pie>
        </Caja>
      );
    case "entrante":
      return <Entrante item={item} />;
    case "automatica":
      return (
        <Caja id={item.id} lado="derecha" cls="bg-white border border-dashed border-slate-200">
          <p className="mb-1 text-[11px] font-semibold text-slate-500">{item.motivo === "confirmacion_baja" ? "Confirmación de baja" : "Respuesta automática"}</p>
          <Texto texto={item.texto} />
          {item.aproximado && (
            <p className="mt-1 text-[11px] italic text-slate-500">Texto aproximado: es el que está configurado hoy (el que salió no quedó guardado).</p>
          )}
          <Pie><span>{horaNegocio(item.at)}</span><ChipEstadoMensaje estado={item.estado} /></Pie>
        </Caja>
      );
    case "manual":
      return <Manual item={item} onReabrir={onReabrir} onReintentar={onReintentar} />;
    default:
      return null;
  }
}

function Entrante({ item }: { item: ItemHiloEntrante }) {
  const u = item.ubicacion;
  const textoBoton = item.boton?.texto || "";
  // En un botón, el Body suele ser el mismo texto del botón: no se repite.
  const texto = item.texto && item.texto !== textoBoton ? item.texto : "";
  return (
    <Caja id={item.id} lado="izquierda" cls="bg-slate-100">
      {(item.esBaja || item.reenviado) && (
        <div className="mb-1 flex flex-wrap gap-1.5">
          {item.esBaja && <span className={`${chip} bg-red-50 text-red-700`}>Pidió la baja</span>}
          {item.reenviado && <span className={`${chip} bg-slate-200 text-slate-600 inline-flex items-center gap-1`}><Forward size={10} /> Reenviado</span>}
        </div>
      )}
      {item.citadoId && (
        <a href={`#item-${item.citadoId}`} className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 underline">
          <CornerDownRight size={11} /> Respondió a este mensaje
        </a>
      )}
      {textoBoton && <p className="text-sm font-medium text-slate-900">Tocó «{textoBoton}»</p>}
      {texto && <Texto texto={texto} />}
      {!texto && !textoBoton && !item.medios.length && !u && (
        <p className="text-sm italic text-slate-500">
          {item.tipoEntrante === "otro" ? "Mandó un mensaje de un tipo que todavía no mostramos." : "(sin texto)"}
        </p>
      )}
      {item.medios.map((m) => <MedioEntrante key={m.indice} medio={m} />)}
      {u && (
        <div className="mt-1 text-sm text-slate-800">
          <p className="inline-flex items-center gap-1"><MapPin size={13} className="text-slate-400" /> Ubicación: {u.etiqueta || u.direccion || `${u.latitud}, ${u.longitud}`}</p>
          <a href={`https://www.google.com/maps?q=${u.latitud},${u.longitud}`} target="_blank" rel="noopener noreferrer"
            className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 underline">
            Abrir en el mapa <ExternalLink size={11} />
          </a>
        </div>
      )}
      <Pie><span>{horaNegocio(item.at)}</span></Pie>
    </Caja>
  );
}

function Manual({ item, onReabrir, onReintentar }: { item: ItemHiloManual; onReabrir: () => void; onReintentar: (texto: string) => void }) {
  // Quién lo mandó: a una persona de un cliente, alguien del equipo es «Equipo Deenex», sin su mail (`nombreDeAutorPara`).
  const equipo = useEsEquipoDeenex();
  const monto = item.costoClienteUSD !== null ? usdPorMensaje(item.costoClienteUSD) : "";
  // Dentro del cupo Meta no cobra, pero el envío sí: decir sólo «gratis» escondía el débito que aparece en Saldo y
  // costos, y «USD 0,0060 · … gratis …» se leía como una contradicción. Se dice qué se cobró y qué no.
  const costo = item.gratisPorCupo === true
    ? (monto ? `Se cobró ${monto} por el envío; Meta no cobró: ${TEXTO_CUPO_SIN_CARGO}` : `Meta no cobró: ${TEXTO_CUPO_SIN_CARGO}`)
    : monto;
  return (
    <Caja id={item.id} lado="derecha" cls="bg-violet-50 border border-violet-100">
      <p className="mb-1 text-[11px] font-semibold text-violet-800">
        {nombreDeAutorPara(item.autor, equipo)}{item.envio === "plantilla" && item.plantilla ? ` · plantilla «${item.plantilla.nombre}»` : ""}
      </p>
      <Texto texto={item.texto} />
      {item.error && (
        <div className="mt-2 rounded-xl bg-white/70 px-3 py-2 text-xs text-red-700">
          <p className="font-semibold">{item.error.titulo}</p>
          <p className="mt-0.5">{item.error.texto}</p>
          {item.error.accion === "plantilla" && (
            <button type="button" onClick={onReabrir} className="mt-1.5 font-semibold text-violet-700 underline">Reabrir con plantilla</button>
          )}
          {item.error.accion === "reintentar" && (
            <button type="button" onClick={() => onReintentar(item.texto)} className="mt-1.5 font-semibold text-violet-700 underline">Volver a intentar</button>
          )}
        </div>
      )}
      <Pie>
        <span>{horaNegocio(item.at)}</span>
        <span>{TEXTO_ESTADO_ENVIO[item.estado] ?? item.estado}</span>
        {costo && <span>{costo}</span>}
      </Pie>
    </Caja>
  );
}

export default Burbuja;
